import { useEffect, useMemo, useRef, useState } from 'react';

export type CommandItem = {
  id: string;
  label: string;
  hint?: string;
  onRun: () => void;
};

export type CommandGroup = {
  name: string;
  items: CommandItem[];
};

export type CommandPaletteProps = {
  open: boolean;
  query?: string;
  groups: CommandGroup[];
  recent?: CommandItem[];
  onQueryChange?: (q: string) => void;
  onClose?: () => void;
};

/**
 * Fuzzy global search / command bar. Opens on `/` per the v2 keyboard
 * map. Renders inside a `<dialog>` so it gets the focus trap and
 * escape-to-close behavior for free in modern browsers — with a manual
 * fallback for keydown so the parent can mirror state.
 */
export function CommandPalette({
  open,
  query = '',
  groups,
  recent,
  onQueryChange,
  onClose,
}: CommandPaletteProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const flat = useMemo(() => {
    const list: { label: string; group: string; item: CommandItem }[] = [];
    const q = query.trim().toLowerCase();
    const accept = (label: string) => !q || label.toLowerCase().includes(q);
    if (!q && recent && recent.length > 0) {
      for (const item of recent) {
        list.push({ label: item.label, group: 'Recent', item });
      }
    }
    for (const g of groups) {
      for (const item of g.items) {
        if (accept(item.label)) list.push({ label: item.label, group: g.name, item });
      }
    }
    return list;
  }, [groups, recent, query]);

  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
      setActiveIndex(0);
    }
  }, [open, query]);

  if (!open) return null;

  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose?.();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(flat.length - 1, i + 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      flat[activeIndex]?.item.onRun();
      onClose?.();
    }
  }

  // Group items into sections in render order, preserving the indices
  // we use for keyboard activation.
  const sections: { name: string; entries: { idx: number; item: CommandItem }[] }[] = [];
  flat.forEach((entry, idx) => {
    const last = sections[sections.length - 1];
    if (last && last.name === entry.group) {
      last.entries.push({ idx, item: entry.item });
    } else {
      sections.push({ name: entry.group, entries: [{ idx, item: entry.item }] });
    }
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      onKeyDown={onKeyDown}
      // Backdrop click closes the palette — only when the click
      // actually lands on the backdrop layer itself (e === currentTarget
      // would also work but onClick at this level fires after any
      // child onClick bubbles up; the inner card stops propagation via
      // a click handler that swallows the bubble). Without this,
      // operators had to hit ESC or hammer the search button to
      // dismiss the modal — native dialog semantics matter here.
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
      className="fixed inset-0 z-30 flex items-start justify-center bg-gray-900/30 p-sp-9"
    >
      <div
        // Stop the click bubbling so clicks inside the card never
        // bubble up to the backdrop handler above and dismiss the
        // modal when the operator was just clicking a result.
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl overflow-hidden rounded-r-card border border-gray-300 bg-gray-50 shadow-lg"
      >
        <label className="block px-sp-4 pt-sp-3">
          <span className="sr-only">Search</span>
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => onQueryChange?.(e.target.value)}
            placeholder="Type to search assets, alerts, sensors…"
            className="w-full border-0 bg-transparent text-fs-statement text-gray-900 outline-none placeholder:text-gray-700"
          />
        </label>
        <div className="border-t border-gray-300" />
        <div className="max-h-80 overflow-y-auto">
          {flat.length === 0 ? (
            <p className="px-sp-4 py-sp-6 text-center text-fs-body text-gray-700">
              {query.trim()
                ? 'No matches. Try a different term.'
                : 'Start typing to search across the fleet.'}
            </p>
          ) : (
            sections.map((section) => (
              <section key={section.name}>
                <p className="px-sp-4 pb-sp-1 pt-sp-3 text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
                  {section.name}
                </p>
                {/* Listbox semantics: role=listbox requires its direct
                    children to be role=option. Wrapping <li> breaks the
                    parent/child requirement and tags the <li> as a
                    stray listitem. Render the option directly. */}
                <div role="listbox" aria-label={section.name}>
                  {section.entries.map(({ idx, item }) => {
                    const active = idx === activeIndex;
                    return (
                      <button
                          key={item.id}
                          type="button"
                          role="option"
                          aria-selected={active}
                          onMouseEnter={() => setActiveIndex(idx)}
                          onClick={() => {
                            item.onRun();
                            onClose?.();
                          }}
                          className={
                            'flex w-full items-center justify-between px-sp-4 py-sp-2 text-left text-fs-body transition-colors focus:outline-none ' +
                            (active
                              ? 'bg-info-bg text-gray-900'
                              : 'text-gray-900 hover:bg-gray-100')
                          }
                        >
                          <span>{item.label}</span>
                          {item.hint ? (
                            <span className="font-mono text-fs-meta text-gray-700">
                              {item.hint}
                            </span>
                          ) : null}
                        </button>
                    );
                  })}
                </div>
              </section>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
